import React, { ReactNode, useMemo, useState } from 'react';

import { Card, Flex, Layout, Steps, StepsProps } from 'antd';

import { UnexpectedTypeError } from 'lib/errors/UnexpectedTypeError';
import { LoadedInvite } from 'pages/invite/invite.type';
import { Domain } from 'types/Domain.type';
import { Guest } from 'types/Guest.type';

import { SignUpInviteDetails } from './SignUpInviteDetails';
import { SignUpInviteTitle } from './SignUpInviteTitle';
import { InfoStep } from './Steps/InfoStep';
import { SignedUpStep } from './Steps/SignedUpStep';
import { SignUpStep } from './Steps/SignUpStep';
import { useSteps } from './Steps/useSteps';

type Props = { invite: LoadedInvite };

type Step = NonNullable<StepsProps['items']>[number] & { content: ReactNode };

export function SignUpInvite({ invite }: Props) {
    const [guest, setGuest] = useState<Guest.Model | null>(null);
    const [rawCpf, setRawCpf] = useState<Domain.RawCpf | null>(null);

    const { currentStep, nextStep, previousStep } = useSteps();

    if (invite.apartment === null)
        throw new UnexpectedTypeError('invite.apartment', 'null', 'Apartment.Model');

    if (invite.author === null)
        throw new UnexpectedTypeError('invite.author', 'null', 'Resident.Model');

    const title = <SignUpInviteTitle clientName={invite.apartment.tower.client.name} />;

    const steps: Step[] = useMemo(() => [
        {
            title: 'Informações',
            content: (
                <InfoStep 
                    invite={invite} 
                    setGuest={setGuest}
                    rawCpf={rawCpf}
                    setRawCpf={setRawCpf}
                    nextStep={nextStep} 
                />
            )
        },
        {
            title: 'Cadastro',
            content: (
                <SignUpStep
                    invite={invite}
                    guest={guest}
                    setGuest={setGuest}
                    rawCpf={rawCpf}
                    nextStep={nextStep}
                    previousStep={previousStep}
                />
            )
        },
        {
            title: 'Acesso liberado',
            content: <SignedUpStep />
        },
    ], [rawCpf, guest, invite, nextStep, previousStep]);

    return (
        <Layout
            data-component="SignUpInvite"
            style={{ 
                minHeight: '100vh', 
                alignItems: 'center', 
                justifyContent: 'center'
            }}
        >
            <Card title={title} style={{ maxWidth: '90vw' }}>
                <Flex vertical gap="large">
                    <Steps items={steps} current={currentStep} />

                    <SignUpInviteDetails invite={invite} />

                    {steps[currentStep].content}
                </Flex>
            </Card>
        </Layout>
    );
}
